// Enhanced Vaanmathi - Dynamic Payslip Record
import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Alert,
  Dropdown,
  Pagination,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Download,
  FileText,
  FileSpreadsheet,
  Calendar,
  MapPin,
  Eye,
  X,
} from "lucide-react";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import { ToastContext } from "../../Context/ToastContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";


const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString)
      .toLocaleDateString("en-GB")
      .split("/")
      .join("-");
  } catch {
    return "Invalid Date";
  }
};


const PayslipRecord = () => {
  const [salaryData, setSalaryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [employeeDetailsMap, setEmployeeDetailsMap] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [availableLocations, setAvailableLocations] = useState([]);

  // Modal state for detailed view
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  const companyKey = sessionStorage.getItem("companyKey");

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (Object.keys(employeeDetailsMap).length > 0) {
      const locations = new Set();
      Object.values(employeeDetailsMap).forEach((details) => {
        if (details.locationName && details.locationName !== "N/A") {
          locations.add(details.locationName);
        }
      });
      setAvailableLocations(Array.from(locations).sort());
    }
  }, [employeeDetailsMap]);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      if (salaryData.length === 0) return;

      const detailsMap = {};

      for (const salary of salaryData) {
        try {
          const metadata = JSON.parse(salary.metadata || "{}");
          const employeeMobile = metadata.employeeMobile;
          const employeeId = metadata.employeeId;

          let response;
          let employeeData;

          if (employeeMobile) {
            response = await fetch(
              `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers?mobile_number=${employeeMobile}`
            );
            employeeData = await response.json();
          }

          if (
            !response?.ok ||
            (Array.isArray(employeeData) && employeeData.length === 0)
          ) {
            if (employeeId) {
              response = await fetch(
                `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers?userId=${employeeId}`
              );
              employeeData = await response.json();
            }
          }

          if (!response?.ok) {
            detailsMap[salary.id] = { locationName: "N/A" };
            continue;
          }

          const employee = Array.isArray(employeeData)
            ? employeeData[0]
            : employeeData;
          detailsMap[salary.id] = {
            locationName: employee?.locationName || "N/A",
          };
        } catch (err) {
          console.error("Error fetching employee details:", err);
          detailsMap[salary.id] = { locationName: "N/A" };
        }
      }

      setEmployeeDetailsMap(detailsMap);
    };

    fetchEmployeeDetails();
  }, [salaryData]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSelectedMonth("");
    setSelectedLocation("");
    setCurrentPage(1);
  };

  const fetchSalaryData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8924_PayrollProduct_PayrollPayslip_PayrollPayslipData_PayrollPayslipDataList"
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch payslip data");
      }

      const processedData = Array.isArray(data)
        ? data.reverse()
        : [data].reverse();
      const filteredData = processedData.filter((d)=> d.companyKey === companyKey);
      setSalaryData(filteredData);
    } catch (err) {
      setError(err.message || "Failed to fetch payslip data");
      console.error("Error fetching payslip data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    })}`;
  };

  const calculateDaysBetween = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end.getTime() - start.getTime();
    const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;
    return dayDifference;
  };

  const getSalaryMonth = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    ) {
      return start.toLocaleString("default", { month: "long" });
    }

    const startMonthDays =
      new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate() -
      start.getDate() +
      1;
    const endMonthDays = end.getDate();

    return startMonthDays >= endMonthDays
      ? start.toLocaleString("default", { month: "long" })
      : end.toLocaleString("default", { month: "long" });
  };

  // Enhanced dynamic parsing function
  const parsePayslipData = (salary) => {
    try {
      const companyInfo = JSON.parse(salary.companyInfo || "{}");
      const employeeSummary = JSON.parse(salary.employeeSummary || "{}");
      const metadata = JSON.parse(salary.metadata || "{}");
      const netPayableAmount = JSON.parse(salary.netPayableAmount || "{}");
      const earnings = JSON.parse(salary.earnings || "{}");
      const deductions = JSON.parse(salary.deductions || "{}");
      const bankDetails = JSON.parse(salary.bankDetails || "{}");
      const dynamicData = JSON.parse(salary.dynamicData || "{}");

      const payPeriodMatch = companyInfo.payPeriod?.match(
        /\((\d{4}-\d{2}-\d{2})\)-\((\d{4}-\d{2}-\d{2})\)/
      );
      const startDate = payPeriodMatch
        ? payPeriodMatch[1]
        : metadata.payPeriodStart;
      const endDate = payPeriodMatch
        ? payPeriodMatch[2]
        : metadata.payPeriodEnd;

      let email = "N/A";
      if (employeeSummary["Email"]?.displayValue) {
        email = employeeSummary["Email"].displayValue;
      } else if (metadata.employeeEmail) {
        email = metadata.employeeEmail;
      } else if (employeeSummary["Email"]?.value) {
        email = employeeSummary["Email"].value;
      }

      // Extract basic salary from earnings
      const basicSalary =
        earnings["Basic"]?.amount || earnings["basic"]?.amount || 0;

      return {
        id: salary.id,
        employee_id:
          employeeSummary["Employee ID"]?.displayValue || metadata.employeeId,
        userName: employeeSummary["Employee Name"]?.displayValue,
        email: email,
        mobileNumber: metadata.employeeMobile,
        startDate: startDate,
        endDate: endDate,
        workingDays: employeeSummary["Paid Days"]?.displayValue || "0",
        overallcalculatedSalary:
          netPayableAmount["Net Payable Amount"]?.amount || 0,
        grossSalary: netPayableAmount["Gross Earnings"]?.amount || 0,
        totalDeductions: netPayableAmount["Total Deductions"]?.amount || 0,
        basicSalary: basicSalary,
        designation: employeeSummary["Designation"]?.displayValue || "N/A",

        // Store all parsed data for detailed view
        companyInfo,
        employeeSummary,
        metadata,
        netPayableAmount,
        earnings,
        deductions,
        bankDetails,
        dynamicData,
      };
    } catch (error) {
      console.error("Error parsing payslip data:", error);
      return {
        id: salary.id,
        employee_id: "N/A",
        userName: "N/A",
        email: "N/A",
        mobileNumber: "N/A",
        startDate: "N/A",
        endDate: "N/A",
        workingDays: "0",
        overallcalculatedSalary: 0,
        grossSalary: 0,
        totalDeductions: 0,
        basicSalary: 0,
        designation: "N/A",
      };
    }
  };


  const renderDynamicTableSection = (title, data) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <div className="mb-4">
        <h6 className="fw-bold text-primary mb-3">{title}</h6>
        <Table striped bordered size="sm" className="mb-2">
          <tbody>
            {Object.entries(data).map(([key, value]) => {
              if (
                title === "Employee Summary" &&
                (key === "Pay Period" || key === "Paid Days")
              ) {
                return null;
              }

              let displayValue = value;

              if (key === "payPeriod" && typeof value === "string") {
                const match = value.match(
                  /\((\d{4}-\d{2}-\d{2})\)-\((\d{4}-\d{2}-\d{2})\)/
                );
                if (match) {
                  const [_, start, end] = match;
                  displayValue = `(${formatDate(start)}) - (${formatDate(end)})`;
                } else {
                  displayValue = value;
                }

              }
              if (key === "generatedAt") {

                displayValue = formatDate(displayValue)
              }
              if (key === "payslipGeneratedAt") {

                displayValue = formatDate(displayValue)
              }
              if (key === "payPeriodStart") {

                displayValue = displayValue.split("-").reverse().join("-")
              }
              if (key === "payPeriodEnd") {

                displayValue = displayValue.split("-").reverse().join("-")
              }
              if (typeof value === "object" && value !== null) {
                if (value.displayValue) displayValue = value.displayValue;
                else if (value.value !== undefined) displayValue = value.value;
                else if (value.amount !== undefined)
                  displayValue = `₹${value.amount}`;
                else displayValue = JSON.stringify(value);
              }

              if (key === "companyLogo" && displayValue) {
                return (
                  <tr key={key}>
                    <td className="text-muted small" style={{ width: "40%" }}>
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </td>
                    <td className="fw-medium">
                      <img
                        src={`https://api.dremerz.net/uploads/${displayValue}`}
                        alt="Company Logo"
                        style={{ maxWidth: "100px", maxHeight: "50px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "inline";
                        }}
                      />
                      <span style={{ display: "none" }}>{displayValue}</span>
                    </td>
                  </tr>
                );
              }


              return (
                <tr key={key}>
                  <td className="text-muted small" style={{ width: "40%" }}>
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </td>
                  <td className="fw-medium">{displayValue || "N/A"}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };



  const filteredSalaryData = salaryData
    .map(parsePayslipData)
    .filter((salary) => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (salary.employee_id &&
            salary.employee_id
              .toString()
              .toLowerCase()
              .includes(searchLower)) ||
          (salary.userName &&
            salary.userName.toLowerCase().includes(searchLower)) ||
          (salary.mobileNumber &&
            salary.mobileNumber.toLowerCase().includes(searchLower)) ||
          (salary.email && salary.email.toLowerCase().includes(searchLower)) ||
          (salary.startDate &&
            salary.startDate.toLowerCase().includes(searchLower)) ||
          (salary.endDate &&
            salary.endDate.toLowerCase().includes(searchLower)) ||
          (salary.workingDays &&
            salary.workingDays
              .toString()
              .toLowerCase()
              .includes(searchLower)) ||
          (salary.overallcalculatedSalary &&
            salary.overallcalculatedSalary
              .toString()
              .toLowerCase()
              .includes(searchLower));
        if (!matchesSearch) return false;
      }

      if (selectedMonth) {
        const salaryMonth = getSalaryMonth(salary.startDate, salary.endDate);
        if (salaryMonth.toLowerCase() !== selectedMonth.toLowerCase()) {
          return false;
        }
      }

      if (selectedLocation) {
        const empDetails = employeeDetailsMap[salary.id] || {
          locationName: "N/A",
        };
        if (empDetails.locationName !== selectedLocation) {
          return false;
        }
      }

      return true;
    });

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredSalaryData.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredSalaryData.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Enhanced Excel Export Function
  const exportToExcel = () => {
    try {
      setExporting(true);

      const exportData = filteredSalaryData.map((salary) => {
        const empDetails = employeeDetailsMap[salary.id] || {
          locationName: "N/A",
        };

        return {
          "Employee ID": salary.employee_id || "N/A",
          Name: salary.userName || "N/A",
          Email: salary.email || "N/A",
          Mobile: salary.mobileNumber || "N/A",
          Designation: salary.designation || "N/A",
          Location: empDetails.locationName,
          "Pay Period Start": formatDate(salary.startDate),
          "Pay Period End": formatDate(salary.endDate),
          "Total Days": calculateDaysBetween(salary.startDate, salary.endDate),
          "Working Days": salary.workingDays || "N/A",
          "Basic Salary (₹)": Math.round(parseFloat(salary.basicSalary || 0)),
          "Gross Salary (₹)": Math.round(parseFloat(salary.grossSalary || 0)),
          "Total Deductions (₹)": Math.round(
            parseFloat(salary.totalDeductions || 0)
          ),
          "Net Salary (₹)": Math.round(
            parseFloat(salary.overallcalculatedSalary || 0)
          ),
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      const colWidths = [
        { wch: 12 }, // Employee ID
        { wch: 20 }, // Name
        { wch: 25 }, // Email
        { wch: 15 }, // Mobile
        { wch: 20 }, // Designation
        { wch: 15 }, // Location
        { wch: 15 }, // Pay Period Start
        { wch: 15 }, // Pay Period End
        { wch: 12 }, // Total Days
        { wch: 12 }, // Working Days
        { wch: 15 }, // Basic Salary
        { wch: 15 }, // Gross Salary
        { wch: 18 }, // Total Deductions
        { wch: 15 }, // Net Salary
      ];
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Payslip Records");

      const currentDate = new Date().toISOString().split("T")[0];
      const filename = searchTerm
        ? `payslip_records_filtered_${currentDate}.xlsx`
        : `payslip_records_${currentDate}.xlsx`;

      XLSX.writeFile(wb, filename);

      addToast &&
        addToast(`Excel file exported successfully: ${filename}`, "success");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      addToast && addToast("Failed to export Excel file", "error");
    } finally {
      setExporting(false);
    }
  };

  const getAvailableMonths = () => {
    const months = new Set();
    salaryData.forEach((salary) => {
      const parsedSalary = parsePayslipData(salary);
      const month = getSalaryMonth(
        parsedSalary.startDate,
        parsedSalary.endDate
      );
      if (month && month !== "N/A") {
        months.add(month);
      }
    });
    return Array.from(months).sort((a, b) => {
      const monthsOrder = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return monthsOrder.indexOf(a) - monthsOrder.indexOf(b);
    });
  };

  const exportToPDF = () => {
    try {
      setExporting(true);

      const doc = new jsPDF("l", "pt", "a4");

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("Payslip Records Report", 40, 40);

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");

      const currentDate = new Date().toLocaleDateString("en-IN");
      doc.text(`Export Date: ${currentDate}`, 40, 65);
      doc.text(`Total Records: ${filteredSalaryData.length}`, 40, 80);

      if (searchTerm) {
        doc.text(`Filter Applied: "${searchTerm}"`, 40, 95);
      }

      const tableColumns = [
        "Employee ID",
        "Name",
        "Email",
        "Pay Period",
        "Basic Salary",
        "Net Salary (₹)",
      ];

      const tableData = filteredSalaryData.map((salary) => [
        salary.employee_id || "N/A",
        salary.userName || "N/A",
        salary.email || "N/A",
        `${formatDate(salary.startDate)} to ${formatDate(salary.endDate)}`,
        `₹${Math.round(parseFloat(salary.basicSalary || 0)).toLocaleString(
          "en-IN"
        )}`,
        `₹${Math.round(
          parseFloat(salary.overallcalculatedSalary || 0)
        ).toLocaleString("en-IN")}`,
      ]);

      doc.autoTable({
        head: [tableColumns],
        body: tableData,
        startY: searchTerm ? 110 : 95,
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 90 },
          2: { cellWidth: 120 },
          3: { cellWidth: 140 },
          4: { cellWidth: 80 },
          5: { cellWidth: 80 },
        },
      });

      const filenameDate = new Date().toISOString().split("T")[0];
      const filename = searchTerm
        ? `payslip_records_filtered_${filenameDate}.pdf`
        : `payslip_records_${filenameDate}.pdf`;

      doc.save(filename);

      addToast &&
        addToast(`PDF file exported successfully: ${filename}`, "success");
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      addToast && addToast("Failed to export PDF file", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = (salary) => {
    setSelectedPayslip(salary);
    setShowDetailModal(true);
  };

  useEffect(() => {
    fetchSalaryData();
  }, []);

  const isMobile = window.innerWidth < 768;

  if (loading) {
    return (
      <div className="container-fluid px-4 py-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary" />
            <p className="mt-3 text-muted">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
        <Button
          variant="primary"
          onClick={() => {
            setError("");
            fetchSalaryData();
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-4">
      <Row className="mb-1">
        <Col className="px-3">
          {/* Header Section */}
          <div className="row align-items-center mb-2 border rounded bg-light py-2">
            <div className="col-6 col-md-3 ">
              <h5 className="mb-0 d-flex flex-start">Payslip Records - Today</h5>
            </div>
            <div className="col-6 col-md-9 text-end">
              {filteredSalaryData.length > 0 && (
                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="outline-primary"
                    id="export-dropdown"
                    disabled={exporting}
                  >
                    <Download size={16} className="me-2" />
                    {exporting ? "Exporting..." : "Export Data"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={exportToExcel} disabled={exporting}>
                      <FileSpreadsheet size={16} className="me-2" />
                      Export as Excel
                    </Dropdown.Item>
                    <Dropdown.Item onClick={exportToPDF} disabled={exporting}>
                      <FileText size={16} className="me-2" />
                      Export as PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row align-items-center mb-3 border rounded bg-light py-2">
            {/* Search input */}
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <Search size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search here..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={clearSearch}
                  disabled={!searchTerm && !selectedMonth && !selectedLocation}
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Filter dropdowns */}
            <div className="col-12 col-md-6">
              <div className="d-flex flex-wrap justify-content-center justify-content-md-end gap-2">
                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="outline-secondary"
                    id="month-filter"
                    className="btn-sm"
                  >
                    <Calendar size={16} className="me-2" />
                    {selectedMonth || "Filter by Month"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setSelectedMonth("")}>
                      All Months
                    </Dropdown.Item>
                    {getAvailableMonths().map((month) => (
                      <Dropdown.Item
                        key={month}
                        active={selectedMonth === month}
                        onClick={() => setSelectedMonth(month)}
                      >
                        {month}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="outline-success"
                    id="location-filter"
                    className="btn-sm"
                  >
                    <MapPin size={16} className="me-2" />
                    {selectedLocation || "Filter by Location"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setSelectedLocation("")}>
                      All Locations
                    </Dropdown.Item>
                    {availableLocations.map((location) => (
                      <Dropdown.Item
                        key={location}
                        active={selectedLocation === location}
                        onClick={() => setSelectedLocation(location)}
                      >
                        {location}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className="row align-items-center justify-content-between mb-2">
            {/* Records per page selector */}
            <div className="col-6 col-md-auto">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Paginate</span>
                <Form.Select
                  size="sm"
                  value={recordsPerPage}
                  onChange={handleRecordsPerPageChange}
                  style={{ width: "80px" }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
              </div>
            </div>

            {/* Pagination controls */}
            <div className="col-6 col-md-auto">
              <Pagination
                size="sm"
                className="mb-0 justify-content-center justify-content-md-end"
              >
                <Pagination.First
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                />

                {/* Mobile: Show simplified pagination */}
                {isMobile ? (
                  <>
                    <Pagination.Item active>{currentPage}</Pagination.Item>
                    <Pagination.Ellipsis disabled />
                    <Pagination.Item onClick={() => paginate(totalPages)}>
                      {totalPages}
                    </Pagination.Item>
                  </>
                ) : (
                  /* Desktop: Show full pagination */
                  Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => paginate(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })
                )}

                <Pagination.Next
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          {isMobile ? (
            <div>
              {/* Mobile Payslip Cards */}
              {currentRecords.length > 0 ? (
                currentRecords.map((salary, index) => {
                  const empDetails = employeeDetailsMap[salary.id] || {
                    locationName: "Loading...",
                  };
                  return (
                    <Card key={salary.id} className="mb-3 shadow-sm">
                      <Card.Body className="p-2">
                        <Table
                          responsive
                          striped
                          bordered
                          size="sm"
                          className="mb-2"
                        >
                          <tbody>
                            <tr>
                              <td className="fw-bold">SI</td>
                              <td>{filteredSalaryData.length - index}</td>
                            </tr>
                            <tr>
                              <td style={{ minWidth: 150 }} className="fw-bold">
                                Month
                              </td>
                              <td>
                                {getSalaryMonth(
                                  salary.startDate,
                                  salary.endDate
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Employee ID</td>
                              <td>{salary.employee_id || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Name</td>
                              <td>{salary.userName || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Mail ID</td>
                              <td>{salary.email || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Location</td>
                              <td>{empDetails.locationName}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Pay Period</td>
                              <td>
                                <div>
                                  ({formatDate(salary.startDate)}) - ({formatDate(salary.endDate)})
                                </div>
                                <small className="text-muted">
                                  {calculateDaysBetween(
                                    salary.startDate,
                                    salary.endDate
                                  )}{" "}
                                  days
                                </small>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Basic Salary</td>
                              <td>
                                ₹
                                {Math.round(
                                  parseFloat(salary.basicSalary || 0)
                                ).toLocaleString("en-IN")}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Net Salary</td>
                              <td>
                                ₹
                                {Math.round(
                                  parseFloat(
                                    salary.overallcalculatedSalary || 0
                                  )
                                ).toLocaleString("en-IN")}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                        <div className="d-flex justify-content-end">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(salary)}
                            className="d-flex align-items-center gap-1"
                          >
                            View Details
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })
              ) : (
                <div className="text-center py-4 text-muted">
                  {searchTerm
                    ? "No matching records found"
                    : "No payslip data available"}
                </div>
              )}
            </div>
          ) : (
            // <Card
            //   className="mb-4"
            //   style={{ width: "100%", overflow: "visible" }}
            // >
            //   <Card.Body style={{ padding: 0, overflowX: "auto" }}>
            <Table
              responsive
              bordered
              striped
              hover
              style={{
                minWidth: "100%",
                marginBottom: 0,
                tableLayout: "auto",
              }}
            >
              <thead>
                <tr>
                  <th id="text-white-important">SI</th>
                  <th id="text-white-important">Month</th>
                  <th id="text-white-important">Employee ID</th>
                  <th id="text-white-important">Name</th>
                  <th id="text-white-important">Mail ID</th>
                  <th id="text-white-important">Location</th>
                  <th id="text-white-important" style={{ minWidth: 230 }}>
                    Pay Period
                  </th>
                  <th id="text-white-important">Basic Salary</th>
                  <th id="text-white-important">Net Salary</th>
                  <th id="text-white-important">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.length > 0 ? (
                  currentRecords.map((salary, index) => {
                    const empDetails = employeeDetailsMap[salary.id] || {
                      locationName: "Loading...",
                    };
                    return (
                      <tr key={salary.id}>
                        <td>
                          {filteredSalaryData.length - index}
                        </td>
                        <td>
                          {getSalaryMonth(salary.startDate, salary.endDate)}
                        </td>
                        <td>{salary.employee_id || "N/A"}</td>
                        <td>{salary.userName || "N/A"}</td>
                        <td>{salary.email || "N/A"}</td>
                        <td>{empDetails.locationName}</td>
                        <td>
                          <div>
                            <div>
                              ({formatDate(salary.startDate)}) - ({formatDate(salary.endDate)})
                            </div>
                            <small className="text-muted">
                              {calculateDaysBetween(salary.startDate, salary.endDate)} days
                            </small>
                          </div>
                        </td>
                        <td>
                          ₹
                          {Math.round(
                            parseFloat(salary.basicSalary || 0)
                          ).toLocaleString("en-IN")}
                        </td>
                        <td>
                          ₹
                          {Math.round(
                            parseFloat(salary.overallcalculatedSalary || 0)
                          ).toLocaleString("en-IN")}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(salary)}
                            className="d-flex align-items-center gap-1"
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      {searchTerm
                        ? "No matching records found"
                        : "No payslip data available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            //   </Card.Body>
            // </Card>
          )}
        </Col>
      </Row>

      {/* Pagination */}
      {filteredSalaryData.length > 0 && (
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-end align-items-center">
              <div className="text-muted">
                Showing {indexOfFirstRecord + 1} to{" "}
                {Math.min(indexOfLastRecord, filteredSalaryData.length)} of{" "}
                {filteredSalaryData.length} records
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Dynamic Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {/* <FaMoneyBill1Wave className="me-2" /> */}
            Payslip Details - {selectedPayslip?.userName || "N/A"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {selectedPayslip && (
            <div>
              {/* Company Information */}
              {selectedPayslip.companyInfo &&
                renderDynamicTableSection(
                  "Company Information",
                  selectedPayslip.companyInfo
                )}

              {/* Employee Summary */}
              {selectedPayslip.employeeSummary &&
                renderDynamicTableSection(
                  "Employee Summary",
                  selectedPayslip.employeeSummary
                )}

              {/* Bank Details */}
              {selectedPayslip.bankDetails &&
                renderDynamicTableSection(
                  "Bank Details",
                  selectedPayslip.bankDetails
                )}

              {/* Earnings Breakdown */}
              {selectedPayslip.earnings &&
                renderDynamicTableSection(
                  "Earnings Breakdown",
                  selectedPayslip.earnings
                )}

              {/* Deductions Breakdown */}
              {selectedPayslip.deductions &&
                renderDynamicTableSection(
                  "Deductions Breakdown",
                  selectedPayslip.deductions
                )}

              {/* Net Payable Amount */}
              {selectedPayslip.netPayableAmount &&
                renderDynamicTableSection(
                  "Net Payable Amount",
                  selectedPayslip.netPayableAmount
                )}

              {/* Metadata Information */}
              {selectedPayslip.metadata &&
                renderDynamicTableSection(
                  "Additional Information",
                  selectedPayslip.metadata
                )}

              {/* Summary Card */}
              <div className="bg-light p-3 rounded mt-4">
                <h6 className="fw-bold mb-3">Summary</h6>
                <div className="row">
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-success mb-1">
                        ₹
                        {Math.round(
                          parseFloat(selectedPayslip.grossSalary || 0)
                        ).toLocaleString("en-IN")}
                      </h5>
                      <small className="text-muted">Gross Salary</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-primary mb-1">
                        ₹
                        {Math.round(
                          parseFloat(selectedPayslip.basicSalary || 0)
                        ).toLocaleString("en-IN")}
                      </h5>
                      <small className="text-muted">Basic Salary</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-danger mb-1">
                        ₹
                        {Math.round(
                          parseFloat(selectedPayslip.totalDeductions || 0)
                        ).toLocaleString("en-IN")}
                      </h5>
                      <small className="text-muted">Total Deductions</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center">
                      <h5 className="text-success mb-1">
                        ₹
                        {Math.round(
                          parseFloat(
                            selectedPayslip.overallcalculatedSalary || 0
                          )
                        ).toLocaleString("en-IN")}
                      </h5>
                      <small className="text-muted">Net Salary</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            <X size={16} className="me-2" />
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PayslipRecord;
